import { useState, useMemo, useCallback } from "react";
import { formatMomentDate } from "../../../utils/dateHelper";
import StatusChip from "../../../components/statusChip/StatusChip";
import { getUserInitial } from "../../../utils/otherHelopers";
import { useDispatch } from "react-redux";
import {
  changePublicStatus,
  fetchPropertyStats,
  deletePublicProperty,
} from "../../../redux/slice/allPropertiesSlice";
import { useToast } from "../../../context/TostContext";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import CustomTable from "../../../components/table/CustomTable";

const NpaListSection = ({
  data,
  navigate,
  currentPage,
  getCustomerActions,
  isPublicView,
  isLoading,
  isInitialLoad,
  searchQuery,
  filterValues,
  canUpdate,
  pagination,
}) => {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const toast = useToast();

  const handlePublicClick = useCallback((item, e) => {
    e.stopPropagation(); // prevent row click navigation
    if (!canUpdate) return;
    setSelectedItem(item);
    setShowModal(true);
  }, [canUpdate]);

  const confirmChange = () => {
    if (selectedItem) {
      if (isPublicView && selectedItem.is_public) {
        dispatch(deletePublicProperty(selectedItem.id))
          .unwrap()
          .then((res) => {
            toast.success(res?.message || "Property successfully unpublished");
            dispatch(fetchPropertyStats());
          })
          .catch((err) => {
            toast.error(
              err?.message ||
              (typeof err === "string"
                ? err
                : "Failed to unpublish property"),
            );
          });
      } else {
        dispatch(
          changePublicStatus({
            ...(isPublicView
              ? { id: selectedItem.property_id }
              : { id: selectedItem.id }),
            is_public: !selectedItem.is_public,
          }),
        )
          .unwrap()
          .then((res) => {
            toast.success(
              res?.message ||
              `Status successfully updated to ${!selectedItem.is_public ? "Published" : "Unpublished"}`,
            );
            dispatch(fetchPropertyStats());
          })
          .catch((err) => {
            toast.error(
              err?.message ||
              (typeof err === "string" ? err : "Failed to update visibility"),
            );
          });
      }
    }
    setShowModal(false);
    setSelectedItem(null);
  };

  const columns = useMemo(
    () => [
      {
        id: "broker_details",
        header: "BROKER DETAILS",
        cell: (_, row) =>
          row?.user ? (
            <div className="d-flex align-items-center gap-2">
              <div className="profile-icon">
                {getUserInitial(
                  `${row?.user?.first_name} ${row?.user?.last_name}`,
                )}
              </div>
              <div className="ms-1">
                <span className="fw-medium text-capitalize">
                  {row?.user?.first_name} {row?.user?.last_name}
                </span>
                <p className="fs-sm">
                  {row?.user?.mobile_number || "-"}
                </p>
                <small>
                  {row?.user?.email || "-"}
                </small>
              </div>
            </div>
          ) : (
            "-"
          ),
      },
      {
        id: "property_type",
        header: "PROPERTY TYPE",
        cell: (_, row) => row?.property_type || "-",
      },
      {
        id: "status",
        header: "STATUS",
        cell: (_, row) =>
          row?.status ? (
            <StatusChip
              bg={
                row?.status === "Close"
                  ? "warning"
                  : row?.status === "Live"
                    ? "success"
                    : row?.status === "Open"
                      ? "blue"
                      : row?.status === "Expired"
                        ? "danger"
                        : ""
              }
              pill
              title={row?.status}
              className="text-capitalize"
            />
          ) : (
            "-"
          ),
      },
      {
        id: "transaction_type",
        header: "TRANSACTION TYPE",
        cell: (_, row) => row?.transaction_type || "-",
      },
      {
        id: "possession",
        header: "POSSESSION",
        cell: (_, row) => row?.possession || "-",
      },
      {
        id: "state",
        header: "STATE",
        cell: (_, row) => row?.state?.name || "-",
      },
      {
        id: "city",
        header: "CITY",
        cell: (_, row) => row?.city?.name || "-",
      },
      {
        id: "district",
        header: "DISTRICT",
        cell: (_, row) => row?.district?.name || "-",
      },
      {
        id: "taluka",
        header: "TALUKA",
        cell: (_, row) => row?.taluka?.name || "-",
      },
      {
        id: "village",
        header: "VILLAGE",
        cell: (_, row) => row?.village?.name || "-",
      },
      {
        id: "locality",
        header: "LOCALITY",
        cell: (_, row) => row?.locality?.name || "-",
      },
      {
        id: "image",
        header: "IMAGE",
        cell: (_, row) =>
          row?.photos ? (
            <img
              src={row?.photos[0]?.url}
              alt="property"
              width={100}
              height={75}
            />
          ) : (
            <span>No Image</span>
          ),
      },
      {
        id: "publish_status",
        header: "PUBLISH STATUS",
        cell: (_, row) => (
          <span onClick={(e) => handlePublicClick(row, e)}>
            <StatusChip
              bg={row.is_public ? "success" : "danger"}
              pill
              title={row.is_public ? "Publish" : "Unpublish"}
              className={canUpdate ? "cursor-pointer" : ""}
            />
          </span>
        ),
      },
      {
        id: "land_type",
        header: "LAND TYPE",
        cell: (_, row) => row?.land_type || "-",
      },
      {
        id: "created_at",
        header: "CREATED ON",
        cell: (_, row) => formatMomentDate(row?.created_at, "DD-MM-YYYY") || "-",
      },
    ],
    [canUpdate, handlePublicClick],
  );

  const actionList = useMemo(
    () =>
      [
        {
          title: "View",
          handler: (row) => {
            const actions = getCustomerActions(row);
            const viewAction = actions.find((a) => a.title === "View");
            if (viewAction && viewAction.handler) viewAction.handler();
          },
        },
        canUpdate && {
          title: "Edit",
          handler: (row) => {
            const actions = getCustomerActions(row);
            const editAction = actions.find((a) => a.title === "Edit");
            if (editAction && editAction.handler) editAction.handler();
          },
        },
        canUpdate && {
          title: (row) => (row.status === "Open" ? "Close" : "Open"),
          handler: (row) => {
            const actions = getCustomerActions(row);
            const toggleAction = actions.find(
              (a) => a.title === "Close" || a.title === "Open",
            );
            if (toggleAction && toggleAction.handler) toggleAction.handler();
          },
        },
        canUpdate && {
          title: (row) => (row.is_public ? "Unpublish" : "Publish"),
          handler: (row) => {
            const actions = getCustomerActions(row);
            const publicAction = actions.find(
              (a) => a.title === "Unpublish" || a.title === "Publish",
            );
            if (publicAction && publicAction.handler) publicAction.handler();
          },
        },
        {
          title: "Delete",
          isVisible: (row) => {
            const actions = getCustomerActions(row);
            return actions.some((a) => a.title === "Delete");
          },
          handler: (row) => {
            const actions = getCustomerActions(row);
            const deleteAction = actions.find((a) => a.title === "Delete");
            if (deleteAction && deleteAction.handler) deleteAction.handler();
          },
        },
      ].filter(Boolean),
    [getCustomerActions, canUpdate],
  );

  const isTableLoading =
    !isInitialLoad &&
    (searchQuery !== filterValues.search ||
      (isLoading && (filterValues.search || searchQuery)));

  return (
    <>
      <CustomTable
        data={data}
        columns={columns}
        loading={isTableLoading}
        keyField="id"
        emptyMessage="No properties found"
        actionList={actionList}
        rowClick={(row) =>
          navigate(`/broker-crm/properties/property-details/${row.id}`, {
            state: {
              id: row?.id,
              currentPage,
              ...(isPublicView ? { is_public: true } : {}),
            },
          })
        }
        pagination={pagination}
      />

      <ConfirmModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={
          selectedItem?.is_public ? "Unpublish Property" : "Publish Property"
        }
        message={
          selectedItem && (
            <p>
              {selectedItem.is_public
                ? "This Property will not be visible to other users once unpublished. Do you want to continue?"
                : "This Property will be visible to all users once published. Do you want to continue?"}
            </p>
          )
        }
        onConfirm={confirmChange}
      />
    </>
  );
};

export default NpaListSection;
